import React, { forwardRef } from 'react';
import { Typography, Divider } from 'antd';
import moment from 'moment';

const { Title, Text } = Typography;

// Print styles and responsive styles
const printStyles = `
    @media print {
        @page {
            margin: 1cm;
            size: A4;
        }
        body {
            margin: 0;
            padding: 0;
        }
        .no-print {
            display: none !important;
        }
    }
    
    @media screen and (max-width: 768px) {
        .invoice-container {
            padding: 15px !important;
        }
        
        .invoice-header {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 15px !important;
        }
        
        .invoice-header-right {
            text-align: left !important;
        }
        
        .invoice-details {
            flex-direction: column !important;
            gap: 15px !important;
        }
        
        .invoice-details-right {
            text-align: left !important;
        }
        
        .buyer-seller-grid {
            grid-template-columns: 1fr !important;
            gap: 20px !important;
        }
        
        .payment-row {
            flex-direction: column !important;
            gap: 5px !important;
        }
        
        .payment-row-text {
            text-align: left !important;
        }
    }
    
    @media screen and (max-width: 576px) {
        .invoice-container {
            padding: 10px !important;
        }
        
        .invoice-title {
            font-size: 20px !important;
        }
        
        .invoice-text {
            font-size: 14px !important;
        }
        
        .invoice-text-strong {
            font-size: 14px !important;
        }
        
        .logo-img {
            max-height: 40px !important;
            max-width: 150px !important;
        }
    }
`;

const Invoice = forwardRef(({ invoiceData }, ref) => {
    const {
        invoiceNumber,
        invoiceDate,
        buyer,
        seller,
        lot,
        auction,
        bidAmount,
        tax = 0,
        totalAmount,
        currency = 'PKR'
    } = invoiceData;

    // Calculate totals
    const subtotal = parseFloat(bidAmount) || 0;
    const taxAmount = parseFloat(tax) || 0;
    const finalTotal = subtotal + taxAmount;

    return (
        <>
            <style>{printStyles}</style>
            <div 
                ref={ref} 
                className="invoice-container"
                style={{ 
                    padding: '30px',
                    maxWidth: '800px',
                    width: '100%',
                    margin: '0 auto',
                    backgroundColor: '#fff',
                    fontFamily: 'Arial, sans-serif',
                    boxSizing: 'border-box'
                }}
            >
            {/* Header */}
            <div className="invoice-header" style={{ 
                marginBottom: '25px', 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center' 
            }}>
                <div>
                    <img 
                        src="/assets/images/logo.png" 
                        alt="Auction Plus Logo"
                        className="logo-img"
                        style={{ 
                            maxHeight: '60px',
                            maxWidth: '250px',
                            height: 'auto',
                            width: 'auto',
                            objectFit: 'contain',
                            display: 'block'
                        }}
                    />
                </div>
                <div className="invoice-header-right" style={{ textAlign: 'right' }}>
                    <Title level={3} className="invoice-title" style={{ margin: 0, color: '#1890ff', marginBottom: '8px' }}>INVOICE</Title>
                    <Text type="secondary" className="invoice-text" style={{ fontSize: '14px' }}>
                        Official Invoice
                    </Text>
                </div>
            </div>

            <Divider style={{ margin: '15px 0' }} />

            {/* Invoice Details */}
            <div className="invoice-details" style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <div>
                    <Text strong className="invoice-text-strong" style={{ fontSize: '16px' }}>Invoice Number:</Text>
                    <div style={{ marginTop: '5px' }}>
                        <Text className="invoice-text" style={{ fontSize: '16px', wordBreak: 'break-word' }}>{invoiceNumber}</Text>
                    </div>
                </div>
                <div className="invoice-details-right" style={{ textAlign: 'right' }}>
                    <Text strong className="invoice-text-strong" style={{ fontSize: '16px' }}>Invoice Date:</Text>
                    <div style={{ marginTop: '5px' }}>
                        <Text className="invoice-text" style={{ fontSize: '16px' }}>
                            {moment(invoiceDate).format('MMMM DD, YYYY')}
                        </Text>
                    </div>
                </div>
            </div>

            <Divider style={{ margin: '15px 0' }} />

            {/* Buyer and Seller Info */}
            <div className="buyer-seller-grid" style={{ marginBottom: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
                <div>
                    <Text strong style={{ fontSize: '16px', display: 'block', marginBottom: '10px' }}>
                        BILL TO (Buyer):
                    </Text>
                    <div style={{ padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                        <Text style={{ display: 'block', marginBottom: '5px' }}>
                            {buyer?.name || 'N/A'}
                        </Text>
                        {buyer?.email && (
                            <Text style={{ display: 'block', marginBottom: '5px', color: '#666' }}>
                                {buyer.email}
                            </Text>
                        )}
                        {buyer?.phone && (
                            <Text style={{ display: 'block', color: '#666' }}>
                                {buyer.phone}
                            </Text>
                        )}
                    </div>
                </div>
                <div>
                    <Text strong style={{ fontSize: '16px', display: 'block', marginBottom: '10px' }}>
                        SOLD BY (Seller):
                    </Text>
                    <div style={{ padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                        <Text style={{ display: 'block', marginBottom: '5px' }}>
                            {seller?.name || 'N/A'}
                        </Text>
                        {seller?.email && (
                            <Text style={{ display: 'block', marginBottom: '5px', color: '#666' }}>
                                {seller.email}
                            </Text>
                        )}
                        {seller?.phone && (
                            <Text style={{ display: 'block', color: '#666' }}>
                                {seller.phone}
                            </Text>
                        )}
                    </div>
                </div>
            </div>

            <Divider style={{ margin: '15px 0' }} />

            {/* Item Details */}
            <div style={{ marginBottom: '20px' }}>
                <Text strong style={{ fontSize: '15px', display: 'block', marginBottom: '12px' }}>
                    ITEM DETAILS:
                </Text>
                <div style={{ 
                    padding: '15px', 
                    backgroundColor: '#f9f9f9', 
                    borderRadius: '4px',
                    border: '1px solid #e8e8e8'
                }}>
                    <div style={{ marginBottom: '10px' }}>
                        <Text strong>Auction: </Text>
                        <Text>{auction?.title || 'N/A'}</Text>
                    </div>
                    <div style={{ marginBottom: '10px' }}>
                        <Text strong>Lot Number: </Text>
                        <Text>#{lot?.lot_number || 'N/A'}</Text>
                    </div>
                    <div style={{ marginBottom: '10px' }}>
                        <Text strong>Lot Title: </Text>
                        <Text>{lot?.title || 'N/A'}</Text>
                    </div>
                    {lot?.description && (
                        <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e8e8e8' }}>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                {lot.description.length > 150 
                                    ? `${lot.description.substring(0, 150)}...` 
                                    : lot.description}
                            </Text>
                        </div>
                    )}
                </div>
            </div>

            <Divider style={{ margin: '15px 0' }} />

            {/* Payment Details */}
            <div style={{ marginBottom: '20px' }}>
                <Text strong style={{ fontSize: '15px', display: 'block', marginBottom: '12px' }}>
                    PAYMENT DETAILS:
                </Text>
                <div style={{ 
                    padding: '15px', 
                    backgroundColor: '#f0f7ff', 
                    borderRadius: '4px',
                    border: '1px solid #91d5ff'
                }}>
                    <div className="payment-row" style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        marginBottom: '10px',
                        paddingBottom: '10px',
                        borderBottom: '1px solid #d9d9d9',
                        alignItems: 'flex-start'
                    }}>
                        <Text className="payment-row-text">Winning Bid Amount:</Text>
                        <Text strong style={{ textAlign: 'right', wordBreak: 'break-word' }}>{currency} {subtotal.toFixed(2)}</Text>
                    </div>
                    {taxAmount > 0 && (
                        <div className="payment-row" style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            marginBottom: '10px',
                            paddingBottom: '10px',
                            borderBottom: '1px solid #d9d9d9',
                            alignItems: 'flex-start'
                        }}>
                            <Text className="payment-row-text">Tax ({taxAmount > 0 ? '5%' : '0%'}):</Text>
                            <Text style={{ textAlign: 'right', wordBreak: 'break-word' }}>{currency} {taxAmount.toFixed(2)}</Text>
                        </div>
                    )}
                    <div className="payment-row" style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        marginTop: '12px',
                        paddingTop: '12px',
                        borderTop: '2px solid #1890ff',
                        alignItems: 'flex-start'
                    }}>
                        <Text strong className="payment-row-text" style={{ fontSize: '16px' }}>TOTAL AMOUNT:</Text>
                        <Text strong style={{ fontSize: '16px', color: '#1890ff', textAlign: 'right', wordBreak: 'break-word' }}>
                            {currency} {finalTotal.toFixed(2)}
                        </Text>
                    </div>
                </div>
            </div>

            <Divider />

            {/* Footer */}
            <div style={{ marginTop: '30px', textAlign: 'center', color: '#666' }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                    This is an official invoice generated by Maveshibids Platform
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                    Generated on {moment().format('MMMM DD, YYYY [at] h:mm A')}
                </Text>
            </div>
        </div>
        </>
    );
});

Invoice.displayName = 'Invoice';

export default Invoice;

